import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { colors } from '../../theme';
import { Navigation } from '../../components/shared/Navigation';
import { ContactInfoQuestion } from './components/questions/ContactInfoQuestion';
import { TopicsQuestion } from './components/questions/TopicsQuestion';
import { AccountsQuestion } from './components/questions/AccountsQuestion';
import { UseCaseQuestion } from './components/questions/UseCaseQuestion';
import { DigestFrequencyQuestion } from './components/questions/DigestFrequencyQuestion';
import { useOnboardingForm } from '../../hooks/useOnboardingForm';

const OnboardingPage: React.FC = () => {
  const location = useLocation();

  // Get email and onboardingId from location state (passed from landing page)
  const email = location.state?.email || '';
  const onboardingId = location.state?.onboardingId;

  const {
    formData,
    setFormData,
    handleSubmit,
    getGoalsLabel,
    showPersonalGoals,
    showBusinessGoals,
    showBusinessName,
  } = useOnboardingForm(email, onboardingId);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.deepNavy }}>
      <Navigation />

      <Container maxWidth="sm" sx={{ paddingTop: '120px', paddingBottom: '64px' }}>
        {/* Header */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 700,
            color: colors.pureWhite,
            marginBottom: '48px',
            textAlign: 'center',
          }}
        >
          Request Early Access
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {/* Email and First Name */}
          <ContactInfoQuestion
            email={formData.email}
            firstName={formData.firstName}
            onEmailChange={(value) => setFormData({ ...formData, email: value })}
            onFirstNameChange={(value) => setFormData({ ...formData, firstName: value })}
          />

          {/* Topics */}
          <TopicsQuestion
            selectedValue={formData.topics}
            customValue={formData.topicsCustom}
            onSelectedChange={(value) => setFormData({ ...formData, topics: value })}
            onCustomChange={(value) => setFormData({ ...formData, topicsCustom: value })}
          />

          {/* Accounts */}
          <AccountsQuestion
            selectedValue={formData.accounts}
            customValue={formData.accountsCustom}
            onSelectedChange={(value) => setFormData({ ...formData, accounts: value })}
            onCustomChange={(value) => setFormData({ ...formData, accountsCustom: value })}
          />

          {/* Use Case */}
          <UseCaseQuestion
            formData={formData}
            onChange={(data) => setFormData({ ...formData, ...data })}
            getGoalsLabel={getGoalsLabel}
            showPersonalGoals={showPersonalGoals}
            showBusinessGoals={showBusinessGoals}
            showBusinessName={showBusinessName}
          />

          {/* Digest Frequency */}
          <DigestFrequencyQuestion
            value={formData.digestFrequency}
            onChange={(value) => setFormData({ ...formData, digestFrequency: value })}
          />

          {/* Submit Button */}
          <Box sx={{ marginTop: '32px' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                backgroundColor: colors.signalOrange,
                color: colors.pureWhite,
                fontWeight: 600,
                padding: '16px 32px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: colors.signalOrangeHover,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${colors.shadows.orange}`,
                },
              }}
            >
              Submit
            </Button>
          </Box>

          {/* Coming Soon Note */}
          <Box
            sx={{
              backgroundColor: colors.whiteAlpha[10],
              border: `1px solid ${colors.whiteAlpha[10]}`,
              borderRadius: '12px',
              padding: '20px',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: colors.slate[400],
                lineHeight: 1.6,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: colors.pureWhite }}>
                Coming soon:
              </Box>{' '}
              Forward filtrd a copy of any email newsletters you follow. filtrd will subscribe to the
              newsletter and include this source in your digests!
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingPage;
